import { Injectable, NgZone, OnDestroy, effect, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { DEPLOYMENT_SSE_EVENT } from '../shared/constants/realtime.constants';
import { AuthService } from './auth.service';
import { DeploymentNotificationService } from './deployment-notification.service';
import { RealtimeEventsService } from './realtime-events.service';

interface AppVersionReadyPayload {
  version?: string;
  buildNumber?: number;
  imageTag?: string;
  environment?: string;
  product?: string;
  status?: string;
  timestamp?: string;
  source?: string;
}

/**
 * Deployment reload wake-up via authenticated SSE (`app_version_ready`).
 */
@Injectable({
  providedIn: 'root',
})
export class DeploymentNotificationHandlerService implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly deploymentNotificationService = inject(DeploymentNotificationService);
  private readonly realtime = inject(RealtimeEventsService);
  private readonly ngZone = inject(NgZone);

  private sseSub: Subscription | null = null;
  private ownsSseConnection = false;

  constructor() {
    effect(() => {
      const user = this.authService.user();
      if (user) {
        this.connect();
      } else {
        this.disconnect();
      }
    });
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  private connect(): void {
    this.connectSse();
  }

  private disconnect(): void {
    this.disconnectSse();
  }

  private connectSse(): void {
    if (this.sseSub) {
      return;
    }
    if (!this.realtime.isConnected() || this.realtime.getAuthMode() !== 'user') {
      this.realtime.connectUser();
      this.ownsSseConnection = true;
    }
    this.sseSub = this.realtime
      .on<AppVersionReadyPayload>(DEPLOYMENT_SSE_EVENT)
      .subscribe((data) => {
        // fetch SSE callbacks run outside Angular; toast needs the zone.
        this.ngZone.run(() => {
          void this.handleDeploymentUpdate(data);
        });
      });
  }

  private disconnectSse(): void {
    this.sseSub?.unsubscribe();
    this.sseSub = null;
    if (this.ownsSseConnection) {
      this.realtime.disconnect();
      this.ownsSseConnection = false;
    }
  }

  private async handleDeploymentUpdate(data: AppVersionReadyPayload): Promise<void> {
    if (!data || typeof data !== 'object') {
      return;
    }

    const status = typeof data.status === 'string' ? data.status.toUpperCase() : '';
    if (status && status !== 'SUCCESS') {
      console.info('[DeploySSE] ignored non-SUCCESS event', data);
      return;
    }

    const sseVersion = typeof data.version === 'string' ? data.version.trim() : '';
    console.info('[DeploySSE] app_version_ready received', { sseVersion, data });

    // Pusher/SSE wake-up; version API is source of truth.
    if (sseVersion) {
      this.deploymentNotificationService.notifyDeployedVersion(sseVersion);
    }
    await this.deploymentNotificationService.checkForNewVersion();
  }
}
